/**
 * GDS Recommendations Component
 *
 * Displays Neo4j Graph Data Science algorithm recommendations:
 * - Centrality algorithms (PageRank, Betweenness, etc.)
 * - Community detection (Louvain, Label Propagation)
 * - Pathfinding (Shortest Path, A*)
 * - Similarity algorithms
 * - Node embeddings
 */

import React, { useState, useMemo } from 'react';
import {
  Sparkles,
  Target,
  Network,
  Route,
  Users,
  Layers,
  ChevronDown,
  ChevronRight,
  Copy,
  Check,
  AlertTriangle,
  Info,
  Zap,
} from 'lucide-react';
import type { GDSRecommendation, GDSAlgorithmCategory, GDSPriority } from '../../types/knowledgeGraphDesign';
import Button from '../ui/Button';
import { Badge } from '../ui/Badge';
import Card from '../ui/Card';

interface GDSRecommendationsProps {
  recommendations: GDSRecommendation[];
  className?: string;
}

// Category icons and colors
const categoryConfig: Record<GDSAlgorithmCategory, { icon: React.ElementType; color: string; label: string }> = {
  centrality: { icon: Target, color: 'text-blue-600 bg-blue-100', label: 'Centrality' },
  community: { icon: Users, color: 'text-purple-600 bg-purple-100', label: 'Community Detection' },
  pathfinding: { icon: Route, color: 'text-green-600 bg-green-100', label: 'Pathfinding' },
  similarity: { icon: Network, color: 'text-orange-600 bg-orange-100', label: 'Similarity' },
  embedding: { icon: Layers, color: 'text-pink-600 bg-pink-100', label: 'Embeddings' },
};

// Priority colors
const priorityColors: Record<GDSPriority, string> = {
  high: 'bg-red-100 text-red-700 border-red-200',
  medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  low: 'bg-green-100 text-green-700 border-green-200',
};

// Complexity indicators
const complexityConfig: Record<string, { label: string; color: string }> = {
  low: { label: 'Simple', color: 'text-green-600' },
  medium: { label: 'Moderate', color: 'text-yellow-600' },
  high: { label: 'Complex', color: 'text-red-600' },
};

export const GDSRecommendations: React.FC<GDSRecommendationsProps> = ({
  recommendations,
  className = '',
}) => {
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [copiedQuery, setCopiedQuery] = useState<string | null>(null);
  const [filterCategory, setFilterCategory] = useState<GDSAlgorithmCategory | 'all'>('all');
  const [filterPriority, setFilterPriority] = useState<GDSPriority | 'all'>('all');

  // Group by category
  const groupedRecommendations = useMemo(() => {
    // Ensure recommendations is an array
    const safeRecommendations = Array.isArray(recommendations) ? recommendations : [];

    const filtered = safeRecommendations.filter((rec) => {
      if (!rec || !rec.algorithm_category) return false;
      if (filterCategory !== 'all' && rec.algorithm_category !== filterCategory) return false;
      if (filterPriority !== 'all' && rec.priority !== filterPriority) return false;
      return true;
    });

    const groups: Record<GDSAlgorithmCategory, GDSRecommendation[]> = {
      centrality: [],
      community: [],
      pathfinding: [],
      similarity: [],
      embedding: [],
    };

    filtered.forEach((rec) => {
      // Only add to group if category exists
      if (rec.algorithm_category && groups[rec.algorithm_category]) {
        groups[rec.algorithm_category].push(rec);
      }
    });

    return groups;
  }, [recommendations, filterCategory, filterPriority]);

  // Toggle card expansion
  const toggleExpand = (algorithmName: string) => {
    setExpandedCards((prev) => {
      const next = new Set(prev);
      if (next.has(algorithmName)) {
        next.delete(algorithmName);
      } else {
        next.add(algorithmName);
      }
      return next;
    });
  };

  // Copy query to clipboard
  const copyQuery = async (query: string, algorithmName: string) => {
    try {
      await navigator.clipboard.writeText(query);
      setCopiedQuery(algorithmName);
      setTimeout(() => setCopiedQuery(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Count by category
  const categoryCounts = useMemo(() => {
    const safeRecommendations = Array.isArray(recommendations) ? recommendations : [];
    return safeRecommendations.reduce((acc, rec) => {
      if (rec && rec.algorithm_category) {
        acc[rec.algorithm_category] = (acc[rec.algorithm_category] || 0) + 1;
      }
      return acc;
    }, {} as Record<string, number>);
  }, [recommendations]);

  // Render recommendation card
  const renderRecommendation = (rec: GDSRecommendation) => {
    const isExpanded = expandedCards.has(rec.algorithm_name);
    // Safely get category config with fallback
    const config = categoryConfig[rec.algorithm_category] || categoryConfig.centrality;
    const Icon = config?.icon || Target;

    return (
      <Card
        key={rec.algorithm_name}
        className="overflow-hidden hover:shadow-md transition-shadow"
      >
        {/* Header */}
        <div
          className="flex items-start gap-3 p-4 cursor-pointer"
          onClick={() => toggleExpand(rec.algorithm_name)}
        >
          <div className={`p-2 rounded-lg ${config.color}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-semibold">{rec.algorithm_name}</h4>
              <Badge className={priorityColors[rec.priority]} variant="outline">
                {rec.priority} priority
              </Badge>
            </div>
            <p className="text-sm text-neutral-600 line-clamp-2">{rec.use_case}</p>
          </div>
          <button className="p-1 hover:bg-neutral-100 rounded">
            {isExpanded ? (
              <ChevronDown className="w-5 h-5 text-neutral-400" />
            ) : (
              <ChevronRight className="w-5 h-5 text-neutral-400" />
            )}
          </button>
        </div>

        {/* Expanded content */}
        {isExpanded && (
          <div className="border-t border-neutral-100 bg-neutral-50 p-4 space-y-4">
            {/* Business value */}
            <div>
              <h5 className="text-sm font-medium text-neutral-700 mb-1 flex items-center gap-1">
                <Zap className="w-4 h-4 text-yellow-500" />
                Business Value
              </h5>
              <p className="text-sm text-neutral-600">{rec.business_value}</p>
            </div>

            {/* Applicable nodes/relationships */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h5 className="text-sm font-medium text-neutral-700 mb-2">Applicable Nodes</h5>
                <div className="flex flex-wrap gap-1">
                  {rec.applicable_nodes.map((node) => (
                    <Badge key={node} variant="outline" className="text-xs">
                      {node}
                    </Badge>
                  ))}
                </div>
              </div>
              <div>
                <h5 className="text-sm font-medium text-neutral-700 mb-2">Applicable Relationships</h5>
                <div className="flex flex-wrap gap-1">
                  {rec.applicable_relationships.map((rel) => (
                    <Badge key={rel} variant="secondary" className="text-xs">
                      {rel}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>

            {/* Complexity */}
            {rec.complexity && complexityConfig[rec.complexity] && (
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-neutral-600">Complexity:</span>
                  <span className={`text-sm font-medium ${complexityConfig[rec.complexity].color}`}>
                    {complexityConfig[rec.complexity].label}
                  </span>
                </div>
              </div>
            )}

            {/* Example query */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h5 className="text-sm font-medium text-neutral-700">Example Query</h5>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    copyQuery(rec.example_query, rec.algorithm_name);
                  }}
                >
                  {copiedQuery === rec.algorithm_name ? (
                    <Check className="w-4 h-4 text-green-500" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
              <pre className="bg-neutral-800 text-neutral-100 p-3 rounded-lg text-xs overflow-x-auto">
                <code>{rec.example_query}</code>
              </pre>
            </div>
          </div>
        )}
      </Card>
    );
  };

  // Safely check for empty recommendations
  const safeRecommendationsCount = Array.isArray(recommendations) ? recommendations.length : 0;

  if (safeRecommendationsCount === 0) {
    return (
      <div className={`text-center py-12 ${className}`}>
        <Sparkles className="w-12 h-12 mx-auto mb-3 text-neutral-300" />
        <p className="text-neutral-500">No GDS recommendations available</p>
        <p className="text-sm text-neutral-400 mt-1">
          Add more tables and relationships to get algorithm suggestions
        </p>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-secondary-500" />
          <h3 className="font-semibold text-lg">GDS Algorithm Recommendations</h3>
          <Badge variant="outline">{safeRecommendationsCount} suggestions</Badge>
        </div>
      </div>

      {/* Category filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4 p-3 bg-neutral-50 rounded-lg">
        <span className="text-sm text-neutral-600">Category:</span>
        <button
          onClick={() => setFilterCategory('all')}
          className={`px-3 py-1 rounded-full text-sm transition-colors ${
            filterCategory === 'all'
              ? 'bg-primary-600 text-white'
              : 'bg-white border border-neutral-200 hover:bg-neutral-100'
          }`}
        >
          All ({safeRecommendationsCount})
        </button>
        {Object.entries(categoryConfig).map(([key, config]) => {
          const count = categoryCounts[key] || 0;
          if (count === 0) return null;
          const Icon = config.icon;
          return (
            <button
              key={key}
              onClick={() => setFilterCategory(key as GDSAlgorithmCategory)}
              className={`px-3 py-1 rounded-full text-sm transition-colors flex items-center gap-1 ${
                filterCategory === key
                  ? 'bg-primary-600 text-white'
                  : 'bg-white border border-neutral-200 hover:bg-neutral-100'
              }`}
            >
              <Icon className="w-3 h-3" />
              {config.label} ({count})
            </button>
          );
        })}

        <div className="w-px h-5 bg-neutral-300 mx-2" />

        <span className="text-sm text-neutral-600">Priority:</span>
        {(['all', 'high', 'medium', 'low'] as const).map((priority) => (
          <button
            key={priority}
            onClick={() => setFilterPriority(priority)}
            className={`px-3 py-1 rounded-full text-sm transition-colors ${
              filterPriority === priority
                ? 'bg-primary-600 text-white'
                : priority === 'all'
                ? 'bg-white border border-neutral-200 hover:bg-neutral-100'
                : `${priorityColors[priority as GDSPriority]} border hover:opacity-80`
            }`}
          >
            {priority === 'all' ? 'All' : priority.charAt(0).toUpperCase() + priority.slice(1)}
          </button>
        ))}
      </div>

      {/* Recommendations by category */}
      <div className="space-y-6">
        {Object.entries(groupedRecommendations).map(([category, recs]) => {
          if (recs.length === 0) return null;
          const config = categoryConfig[category as GDSAlgorithmCategory];
          const Icon = config.icon;

          return (
            <div key={category}>
              <div className="flex items-center gap-2 mb-3">
                <div className={`p-1.5 rounded ${config.color}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <h4 className="font-medium">{config.label}</h4>
                <Badge variant="outline" className="text-xs">
                  {recs.length}
                </Badge>
              </div>
              <div className="grid gap-3">
                {recs.map(renderRecommendation)}
              </div>
            </div>
          );
        })}
      </div>

      {/* Help section */}
      <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h5 className="font-medium text-blue-800 mb-1">About GDS Algorithms</h5>
            <p className="text-sm text-blue-700">
              Neo4j Graph Data Science (GDS) provides powerful algorithms for analyzing graph data.
              These recommendations are based on your schema structure and can help uncover insights
              like influential nodes, communities, and optimal paths.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GDSRecommendations;
